/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Artifact identity — the one projection every surface renders beside a name** (`P3a`, S11).
 *
 * Ratified by Todd, 2026-08-20, and the scope clause is the whole rule:
 *
 * > *"It must be extremely clear to the user, on lists/rows, edit pages, etc… **any place we display
 * > the name of the artifact, the badge should be near by. It's an implicit part of the name
 * > conceptually.**"*
 *
 * So this is a projection, not a component dropped into a page — the same rule as
 * `isNavItemVisible`: a surface must not be able to say something another surface hides. A list row,
 * a detail header, a picker option and a wizard step render identity from here, or they will
 * disagree, and the one that disagrees will be the one somebody is looking at.
 *
 * ## What it can say, and why two facts rather than one
 *
 * | The artifact | Chip |
 * |---|---|
 * | **is** a template | `Template` — a declaration of what this thing *is* |
 * | **extends** something | `Extends {parent}`, or `From {template}` where that parent is a template |
 * | both | both chips — a publisher's own template overriding a system one is an ordinary state |
 *
 * They compose because `isTemplate` and `parentId` are independent: being a baseline and being built
 * from one are different facts, and collapsing them would make the publisher-override case
 * unsayable.
 *
 * ## Lineage stops at one hop, deliberately
 *
 * > *"I don't know the author needs to know every step the doc chain — just, they got 'a bunch of
 * > stuff' from the parent… **Fixing the parents is the parent's owner's job, not the operator's.**"*
 *
 * A child layers over its parent's *resolved* document, so from where the operator stands everything
 * they did not state came from their parent. Naming a grandparent points them at something that is
 * not theirs to fix — and because visibility is per artifact, an ancestor further up may be one they
 * **cannot see**, so naming it would disclose an artifact they have no access to. One hop cannot leak.
 *
 * **The exception is a template**, because a template is a baseline somebody *chose* rather than an
 * accident of someone else's authoring, and its name is what explains the configuration in front of
 * them. That is why {@link ArtifactIdentityChip} distinguishes the two rather than saying `Extends`
 * for both.
 */

import type { ValueProvenance } from '../components/shared/editors/parameter-editor.adapters';

/** The minimum an artifact must expose to have an identity rendered. */
export interface IdentifiableArtifact {
  id?: string | number;
  name?: string;
  parentId?: string | null;
  /**
   * **Where this artifact was copied from** — a fact, not a live link (`#1176`).
   *
   * *Create from template means copy* (Todd, 2026-08-26), so a source built from a template has an
   * `origin` and **no** `parentId`. Without reading it here the one gesture an operator is most
   * likely to use would record its template and still say nothing on screen.
   */
  origin?: string | null;
  isTemplate?: boolean;
}

/** One chip to render beside the name. */
export interface ArtifactIdentityChip {
  /**
   * The design-system provenance member, so the chip's treatment comes from the ratified
   * vocabulary rather than from a colour chosen here.
   *
   * **`template` is used for both *"I am a template"* and *"this came from a template"*,
   * deliberately.** One visual means *template*; a second treatment for the same concept is how a
   * vocabulary stops being one.
   */
  provenance: ValueProvenance;
  label: string;
  /** The artifact this chip points at, when it points at one — for a link, never for the text. */
  targetId?: string;
  /**
   * Where the chip points **outside** the console — today the vendor documentation for an applied
   * template (`#1115`).
   *
   * The operator is being asked to finish a configuration whose endpoint is a `{placeholder}` shape
   * and several of whose parameters are that vendor's own field names, on a screen they reached
   * several steps ago. The chip already says *whose*; this is what makes that actionable.
   *
   * Deliberately not `targetId`, which addresses an artifact **inside** the console and is a
   * different destination. Worth noting that `targetId` is currently declared and read by nothing —
   * adding a second unread field beside it is what this avoids, so `href` ships with its renderer.
   */
  href?: string;
}

/**
 * Resolve a parent's `objectId` to something an operator recognises.
 *
 * Returns `undefined` when the parent is unknown **or not visible to this viewer**, and the caller
 * falls back to an unnamed label. That fallback is not a nicety: naming an artifact the viewer
 * cannot see is a disclosure, and a lookup that silently returns an id would put a UUID on screen
 * where a name belongs.
 */
export type ParentNameLookup = (objectId: string) => string | undefined;

/**
 * Every chip that belongs beside this artifact's name, in render order.
 *
 * Order is *what it is* before *where it came from* — an operator scanning a list is answering
 * "can I use this?" before "where did this come from?", and a template is the answer that changes
 * what they do next.
 */
export function artifactIdentityChips(
  artifact: IdentifiableArtifact | null | undefined,
  lookupParentName?: ParentNameLookup,
  parentIsTemplate?: (objectId: string) => boolean,
  /**
   * A catalog preset applied **in this editing session**, for a surface that has one (#1122/#1115).
   *
   * It is not a stored relationship and must not be mistaken for one: a preset is a catalog entry,
   * so nothing on the saved artifact records that it was used — which is #1115's complaint, and the
   * reason this is a parameter rather than a field on {@link IdentifiableArtifact}. Passing it says
   * *"I know this right now"*, and a surface that does not know passes nothing rather than guessing.
   *
   * It chips through the **same** `template` vocabulary as a template parent, because to the
   * operator it is the same fact — *this configuration came from a baseline somebody chose*. When
   * #1090 makes presets template artifacts, a preset becomes an ordinary `parentId` and this
   * parameter goes away without the chip changing.
   */
  appliedTemplateName?: string,
  /** The applied template's documentation, where it publishes one (`#1115`). */
  appliedTemplateUrl?: string,
): ArtifactIdentityChip[] {
  if (!artifact && !appliedTemplateName) return [];
  const chips: ArtifactIdentityChip[] = [];

  if (artifact?.isTemplate) {
    chips.push({ provenance: 'template', label: 'Template' });
  }

  const parentId = artifact?.parentId;
  /**
   * **A copy of a template says where it came from too** (`#1176`).
   *
   * `parentId` is the live link and wins where it exists — it is the stronger claim, and the two
   * are never both meaningful about the same relationship. `origin` is the weaker one: *this was
   * copied from that*, tracking nothing.
   *
   * It is read **only when the origin is a template**, which is not a narrowing for its own sake:
   * every ordinary Duplicate also stamps `origin`, so rendering it unconditionally would put a
   * *"From X"* chip on every copied source in the product — a chip that says nothing an operator
   * acts on, and noise is how a useful mark stops being read.
   */
  const originId = artifact?.origin && parentIsTemplate?.(artifact.origin) ? artifact.origin : undefined;
  const relationId = parentId ?? originId;

  if (relationId) {
    const parentName = lookupParentName?.(relationId);
    const fromTemplate = parentIsTemplate?.(relationId) ?? false;

    // `From {name}` for a template, `Extends {name}` for an ordinary parent — the distinction the
    // one-hop rule turns on. Unnamed falls back to the bare form, which discloses nothing and is
    // still the useful half: the operator learns this is not a standalone artifact.
    chips.push({
      provenance: fromTemplate ? 'template' : 'inherited',
      label: parentName
        ? `${fromTemplate ? 'From' : 'Extends'} ${parentName}`
        : fromTemplate
          ? 'From a template'
          : 'Extends a parent',
      targetId: parentName ? relationId : undefined,
    });
  } else if (appliedTemplateName) {
    // Only where there is no parent: a stored link is the stronger claim and must win. Rendering
    // both would put two answers to "what is this built from" beside one name.
    chips.push({ provenance: 'template', label: `From ${appliedTemplateName}`, href: appliedTemplateUrl });
  }

  return chips;
}

/**
 * What a multi-step surface shows before the artifact has a name (#1115).
 *
 * > *"We don't currently have any 'title bar' on the wizard that grounds the user to the next level
 * > details of what they're doing… Before the source is named, the 'title bar' could just show
 * > 'ORTB Demand Source - Publica Template'."*
 *
 * The wizard is where identity matters most and is stated least: for the first two steps there is no
 * name for a chip to sit beside, while the operator finishes a configuration whose protocol and
 * template they chose several screens ago.
 *
 * Returns the **name once there is one** — the title bar does not keep saying the type after the
 * operator has told us what to call it; the chips carry the rest.
 */
export function artifactTitle(artifact: IdentifiableArtifact | null | undefined, typeLabel: string): string {
  const name = artifact?.name?.trim();
  return name && name.length > 0 ? name : typeLabel;
}

/**
 * ## Authoring the id — the other half of identity, added 2026-08-24 (`A7`, `#1147`/`#1148`)
 *
 * Everything above renders identity. What follows **authors** it, and it lives in the same file
 * because it is the same subject: opening `artifact-id.ts` beside `artifact-identity.ts` would be
 * two documents answering *what is an artifact's identity* — the C2 violation in miniature.
 *
 * A7's three notions are `id` (authored, immutable, unique — the tag referenced elsewhere,
 * including programmatically), `name` (the descriptive title) and `object_id` (ours, for joins).
 * These helpers are about the first.
 *
 * **Why one projection rather than a check per surface.** The id shape `^[a-z0-9-]+$` was written
 * out four times in this app — the three strategy/rule-set pages and the demand-source wizard — and
 * the slug that proposes one was written **seven** times, once per service. That is the same
 * liberality A7 removed from resolution arriving in the authoring layer: nothing was wrong with any
 * single copy, and there was no way to change what an id may contain without finding all eleven.
 * The clone dialog would have been the eighth and twelfth.
 */

/**
 * What an artifact id may contain.
 *
 * Lowercase letters, numbers and hyphens — it is referenced by callers and appears in URLs, so it
 * has to survive being one. Mirrored by `admin-api.controller.ts`; the console mirrors wire
 * contracts rather than importing them (see `types/demand-source.ts`), and this is one.
 */
export const ARTIFACT_ID_PATTERN = /^[a-z0-9-]+$/;

/**
 * The id a name suggests — **a suggestion, never a derivation.**
 *
 * Callers stop asking once the operator has typed in the id box: an id that keeps re-deriving from
 * the name is an id that changes, which is the one thing an authored identifier must never do.
 *
 * Returns `''` for a name with nothing slug-able in it, so the caller decides what to do with
 * nothing rather than receiving a fabricated `artifact-1787…`.
 */
export function slugifyArtifactId(name: string): string {
  return String(name ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * **What an id is for, said truthfully for the thing being created** (`#1045`, 2026-08-26).
 *
 * Three surfaces each wrote their own sentence and all three said *"in strategies, and by callers"*.
 * On a **template** both halves are false: `isSelectable` excludes it, so no strategy can reference
 * it and no caller can name it in a request — the hint told the operator what an id does for a
 * different artifact than the one in front of them.
 *
 * **And "by the artifacts that extend it" would be false too**, which is the more interesting half.
 * `parentId` holds the parent's **`objectId`**, not its authored key, deliberately — *"so renaming a
 * parent does not orphan its children"* (`ArtifactDto.parentId`). A template's children do not
 * reference its id at all. What does: a link to it, and the key it exports and imports under.
 *
 * One function rather than three strings, because it is one concept with a parameter — and because
 * three copies of a sentence about one thing is how they came to disagree in the first place.
 */
export function artifactIdPurpose(kind: 'artifact' | 'template'): string {
  return kind === 'template'
    ? 'How this template is addressed — in a link to it, and in exports and backups.'
    : 'How this is referenced elsewhere — in request strategies, and by callers naming it in a request.';
}

/**
 * The constraint, as a sentence.
 *
 * It read `". Lowercase letters, numbers and hyphens."` — a noun phrase bolted onto the end of
 * another sentence, which states a rule without saying it *is* one and never says why it exists.
 * An imperative with its reason is the same length and is actually a claim the operator can act on.
 */
export const ARTIFACT_ID_CONSTRAINT_HINT = 'Use lowercase letters, numbers and hyphens — it appears in URLs.';

/**
 * **On the surface: that it is frozen, and nothing else** (`#1409`, Todd 2026-09-22, user-testing the
 * terms editor: *"help text under ID should only state 'Cannot be changed after creation'"*).
 *
 * It carried three clauses — the fact, its reason, and the way round it — under a control on a phase
 * whose density was the complaint. The fact is the one thing an operator **cannot learn by trying**
 * (`ST12` item 5), so it stays; the reason and the redirect move to {@link ARTIFACT_ID_FROZEN_HELP}
 * behind the `?`, which `3c-ii` made reachable without a mouse.
 *
 * The same sentence in create and edit mode on purpose: what it says is true before the save, which
 * is the moment it can still be acted on.
 */
export const ARTIFACT_ID_FROZEN_HINT = 'Cannot be changed after creation.';

/**
 * Why it is frozen, and what to do instead — behind the `?`.
 *
 * *Duplicate to create a variant* is a **redirect**, so it is held to the rule redirects are held to:
 * the gesture it names exists on the list and, since `ST12` item 2, in the editor's own footer as
 * *Save as…*. It is also said where an operator actually collides with the rule — the server's
 * id-taken refusal — because a sentence read only by somebody browsing help is not the sentence that
 * reaches the person who needs it.
 */
export const ARTIFACT_ID_FROZEN_HELP =
  'Other artifacts and callers reference it by this, so renaming it would break them. Duplicate this source to create a variant with a different id.';

/**
 * Why this id is not acceptable, or `undefined` when it is.
 *
 * Returns the **message**, not a boolean, so every surface says the same sentence — #1045's rule
 * applied to a validator: four surfaces each phrasing the same rule is four chances to describe a
 * mechanism instead of an outcome.
 *
 * It does **not** answer uniqueness. Uniqueness is a property of the whole store and a client sees a
 * visibility-filtered slice of it, so a console-side answer would be confidently wrong about exactly
 * the artifacts it cannot see — the hole `isDemandSourceIdTaken` was written to close.
 */
export function validateArtifactId(id: string): string | undefined {
  const trimmed = String(id ?? '').trim();
  if (!trimmed) return 'ID is required.';
  if (!ARTIFACT_ID_PATTERN.test(trimmed)) return 'ID must be lowercase letters, numbers and hyphens only.';
  return undefined;
}

/**
 * The name a copy starts with — `X (Variant)` for an extended copy, `X (Copy)` for an independent
 * one, disambiguated against names already in use.
 *
 * **`extend` names the relationship rather than the act**, because that is what the operator reads
 * later on the child's own page: *"Variant of X"* is what it **is**, *"(Copy)"* is what happened.
 *
 * **Disambiguated rather than refused** (`#1146`): an operator pressing Duplicate has not made a
 * mistake, so where `House (Variant)` is taken the answer is `House (Variant) 2`. It is a
 * *proposal* — the dialog puts it in an editable field, so the operator can say something better.
 *
 * `taken` is what the caller can see, which is a visibility-filtered list. That is honest for a
 * suggestion and would not be for enforcement; duplicate names are a legibility annoyance rather
 * than a correctness hazard now that A7 has made nothing resolve by name.
 */
export function proposeCopyName(sourceName: string, suffix: string, taken: Iterable<string> = []): string {
  return disambiguateName(`${String(sourceName ?? '').trim() || 'Untitled'} (${suffix})`, taken);
}

/** The word a template's name carries, and the only one this rule adds or removes. */
const TEMPLATE_WORD = 'Template';

/**
 * A trailing `Template`, however it was cased.
 *
 * `\s+` rather than `\b` on purpose, twice over: `Templated` keeps its word, and a name that is
 * **only** the word keeps it too — the rule strips a trailing *qualifier*, and there is no stem
 * under that one. Proposing `Untitled` from something that has a name is the worse answer.
 */
const TRAILING_TEMPLATE = /\s+template\s*$/i;

/**
 * **The name a new artifact starts with, which depends on the direction it is going** (`ST12` item 3,
 * ruled by Todd 2026-09-22 on `#1358`).
 *
 * The first build read Todd's *"the suggestion should not include Template"* as a rule about
 * templates and dropped the word everywhere. It is a rule about a **direction**: the word is dropped
 * going *out* of a template and added going *in* to one. Three of them, in his words:
 *
 * | From → to | Rule | Example |
 * | --- | --- | --- |
 * | template → **source** | *Template* is removed | `SpringServe Template` → `SpringServe` |
 * | source → **template** | *Template* is added, **once** | `SpringServe` → `SpringServe Template` |
 * | template → **template** | it is already there, so the relation names the copy | `SpringServe Template` → `SpringServe Template (Variant)` |
 *
 * And the fourth cell, unchanged: source → source is `X (Copy)` / `X (Variant)`.
 *
 * > **The word does the work the suffix does, so they are not both applied.** Going into a template
 * > from a source, `Template` is already what distinguishes the new name from the old one — adding
 * > `(Variant)` as well would give `SpringServe Template (Variant)` for a first template, which is
 * > the `(Template)` suffix this ruling removed wearing a different hat. Between two templates the
 * > word is on both, so the suffix is the only thing left that can tell them apart.
 *
 * `relation` is `null` where the act is not a copy at all — applying a template while **creating**
 * a source is the case, and there is no relationship to name.
 *
 * A **proposal**, disambiguated rather than refused, in an editable box: *a default that cannot be
 * accepted is not a default.*
 */
export function proposeArtifactName(proposal: {
  sourceName: string;
  sourceIsTemplate: boolean;
  targetIsTemplate: boolean;
  /** `Variant` or `Copy` where this is a copy of something; `null` where it is not. */
  relation: string | null;
  taken?: Iterable<string>;
}): string {
  const { sourceName, sourceIsTemplate, targetIsTemplate, relation, taken = [] } = proposal;
  const stem = String(sourceName ?? '')
    .trim()
    .replace(TRAILING_TEMPLATE, '')
    .trim();
  const base = targetIsTemplate ? `${stem || 'Untitled'} ${TEMPLATE_WORD}` : stem || 'Untitled';

  // The suffix names a *relationship*, so it is dropped exactly where the word `Template` has
  // already made the name new — see the table above.
  const suffixed = relation && !(targetIsTemplate && !sourceIsTemplate) ? `${base} (${relation})` : base;
  return disambiguateName(suffixed, taken);
}

/**
 * **A name that is not one the operator can already see**, suffixed with a number where it is
 * (`ST12` item 3, `#1359`).
 *
 * Extracted from {@link proposeCopyName} because the wizard proposes a name too — from a pasted
 * tag's host — and did so without consulting anything, so the one surface an operator meets **first**
 * was the only one whose suggestion could be refused at save. One rule, three places: this, the clone
 * dialog, and the server, which stays the authority because a client holds a visibility-filtered
 * slice of the store.
 *
 * **A default that cannot be accepted is not a default** — it is a prompt with extra steps.
 */
export function disambiguateName(base: string, taken: Iterable<string> = []): string {
  const used = new Set(
    Array.from(taken, (n) =>
      String(n ?? '')
        .trim()
        .toLowerCase(),
    ),
  );
  if (!used.has(base.toLowerCase())) return base;
  let n = 2;
  while (used.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
}
